import type React from "react";
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import { updateMyProfileApi, updateUserProfileApi } from "../../api/usersApi";
import { getClientTimezone } from "../../types/user";
import type { ProfileSectionProps } from "./profileSectionTypes";

type TranslationKeys = keyof typeof translations.en;

const getTimezoneOptions = (): string[] => {
  try {
    return (Intl as any).supportedValuesOf?.("timeZone") ?? [];
  } catch {
    return [];
  }
};

const ProfileTimezoneSection: React.FC<ProfileSectionProps> = ({
  user,
  canEdit,
  isSelf,
  onUserUpdated,
}) => {
  const { t } = useTranslation();
  const browserTimezone = getClientTimezone();
  const timezoneOptions = useMemo(() => {
    const options = getTimezoneOptions();
    if (options.length === 0) {
      return [browserTimezone, "Asia/Jakarta", "UTC"].filter(
        (value, index, arr) => arr.indexOf(value) === index,
      );
    }
    return options;
  }, [browserTimezone]);

  const [manualOverride, setManualOverride] = useState(Boolean(user.timezoneManual));
  const [timezone, setTimezone] = useState(user.timezone || browserTimezone);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setManualOverride(Boolean(user.timezoneManual));
    setTimezone(user.timezone || browserTimezone);
  }, [user.timezone, user.timezoneManual, browserTimezone]);

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const payload = manualOverride
        ? { timezone, timezoneManual: true }
        : { timezone: browserTimezone, timezoneManual: false };
      const updated = isSelf
        ? await updateMyProfileApi(payload)
        : await updateUserProfileApi(user._id, payload);
      onUserUpdated(updated);
      setSuccess(true);
    } catch {
      setError(t("profile.timezone.saveFailed" as TranslationKeys));
    } finally {
      setLoading(false);
    }
  };

  if (!canEdit) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          {t("Timezone" as TranslationKeys)}
        </Typography>
        <Typography variant="body1">{user.timezone || "—"}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t("Timezone" as TranslationKeys)}
      </Typography>
      <FormControlLabel
        control={
          <Switch checked={manualOverride} onChange={(e) => setManualOverride(e.target.checked)} />
        }
        label={t("profile.timezone.override" as TranslationKeys)}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t("profile.timezone.browser" as TranslationKeys)}: {browserTimezone}
      </Typography>
      <Stack spacing={2} direction={{ xs: "column", sm: "row" }} alignItems="flex-start">
        <Autocomplete
          options={timezoneOptions}
          value={timezone}
          onChange={(_event, value) => setTimezone(value || browserTimezone)}
          disabled={!manualOverride}
          fullWidth
          renderInput={(params) => (
            <TextField {...params} label={t("Timezone" as TranslationKeys)} size="small" />
          )}
        />
        <Button variant="contained" onClick={handleSave} disabled={loading} sx={{ minWidth: 100 }}>
          {t("Save" as TranslationKeys)}
        </Button>
      </Stack>
      {error ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mt: 1 }}>
          {t("Success" as TranslationKeys)}
        </Alert>
      ) : null}
    </Box>
  );
};

export default ProfileTimezoneSection;
